import { isApiError } from "../auth/apiClient";
import type { QueueTreeNode } from "../queues";

// Translate a queue-load failure into copy that explains *why* the user
// can't see the queue, not the HTTP status code that delivered the news.
//   - 403: server-side authorization decision. The user is signed in
//     but isn't a member / owner / admin of the queue. Most common
//     cause is following a shared link to a peer's queue.
//   - 404: the queue id doesn't resolve. Either the queue was deleted
//     or the id was mistyped in a hand-edited URL.
//   - everything else: keep the technical detail; an unexpected status
//     here is a bug worth surfacing rather than smoothing over.
export function describeQueueLoadFailure(error: Error): string {
    if (isApiError(error)) {
        if (error.status === 403) {
            return "You don't have access to this queue. Ask one of its owners to add you, or pick a queue from your list.";
        }
        if (error.status === 404) {
            return "This queue no longer exists. It may have been deleted.";
        }
    }
    return `Couldn\u2019t load this queue. ${error.message}`;
}

// Flattens a queue tree into the ids of every workable node. Category
// nodes are excluded because they never carry work items themselves
// (they only group their children) — including them would inflate the
// query string for no UI benefit.
export function collectWorkableQueueIds(roots: readonly QueueTreeNode[]): readonly string[] {
    const ids: string[] = [];
    const stack: QueueTreeNode[] = [...roots];
    while (stack.length > 0) {
        const node = stack.pop();
        if (node === undefined) continue;
        if (node.queue.isWorkable && node.queue.status === "active") {
            ids.push(node.queue.id);
        }
        for (const child of node.children) stack.push(child);
    }
    return ids;
}
